import React, { useEffect, useState } from 'react';
import Button from '../../components/button';
import Select from '../../components/select';
import Input from '../../components/input';
import Error from '../../components/error_message';
import RadioButtons from '../../components/radio_buttons';
import { useAngularService } from 'helpers/get_angular_service';
import notification from 'helpers/notification';
import setValue from 'helpers/set_value';

const GymProfileGeneral = ({ getProfile, setTab, gym }) => {
  const [profile, setProfile] = useState<any>({});
  const [errors, setErrors] = useState<any>({});
  const [isEmailAuthorized, setIsEmailAuthorized] = useState(false);
  const loadingAnimation = useAngularService('loadingAnimation');
  const $http = useAngularService('$http');
  const $rootScope = useAngularService('$rootScope');
  const GTMService = useAngularService('GTMService');

  useEffect(() => {
    loadingAnimation.show();
    getProfile().then((response) => {
      setProfile(response.data);
      loadingAnimation.hide();
    });
  }, []);

  const defaultReplyAddresses = gym.reply_email_addresses.map((item) => item.email);

  const save = () => {
    const url = `gyms/${gym.id}`;
    loadingAnimation.show();
    GTMService.trackPage('Account Profile - General', 'attempt');
    $http.put(url, { gym: profile }).then(
      (response) => {
        GTMService.trackPage('Account Profile - General', 'success');
        loadingAnimation.hide();
        $rootScope.gym.units.mass = response.data.mass;
        $rootScope.gym.units.measurement = response.data.measurement;
        $rootScope.gym.name = profile.name;
        $rootScope.gym.gym_contact_detail.default_from_address =
          response.data.gym_contact_detail_attributes.default_from_address;
        notification.success('Saved successfully');
        setErrors({});
      },
      (response) => {
        GTMService.trackPage('Account Profile - General', 'error');
        notification.error('Save failed. Please fix the errors below');
        setErrors(response.data.errors);
        loadingAnimation.hide();
      },
    );
  };

  const onValueChange = (value, path) => {
    const updated = setValue(profile, path, value);
    setProfile(updated);
  };

  const checkEmailAuthorization = (email) => {
    const isEmailAuthorized = gym.reply_email_addresses.find((item) => item.email === email);
    if (isEmailAuthorized) {
      return isEmailAuthorized.authorized;
    } else {
      return false;
    }
  };

  useEffect(() => {
    if (!profile?.gym_contact_detail_attributes?.default_from_address) return;
    const isEmailAuthorized = checkEmailAuthorization(
      profile?.gym_contact_detail_attributes?.default_from_address,
    );
    setIsEmailAuthorized(isEmailAuthorized);
  }, [profile?.gym_contact_detail_attributes?.default_from_address]);

  return (
    <div className="content-box first-one">
      <p>
        Manage your account settings,{' '}
        <a onClick={() => setTab('autopayments')}>membership payments</a>,{' '}
        <a onClick={() => setTab('contact_details')}>contact information</a> &amp;{' '}
        <a onClick={() => setTab('memberships')}>tax rates</a>.
      </p>
      <div className="form-group row">
        <div className="col-sm-3">
          <label className="control-label">Business Name</label>
        </div>
        <div className="col-sm-9">
          <div className="form-group">
            <Input value={profile.name} onChange={(val) => onValueChange(val, 'name')} />
            <Error error={errors.name} />
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-sm-3">
          <label>Region Settings</label>
        </div>
        <div className="col-sm-3">
          <div className="form-group pre-populated-fields">
            <label className="control-label">Currency</label>
            <Input value={gym.currency_symbol_with_code} disabled />
          </div>
        </div>
        <div className="col-sm-3 pre-populated-fields">
          <div className="form-group">
            <label className="control-label select">Country</label>
            <div className="worx-select-control">
              <select className="form-control" disabled value={gym.country}>
                <option>{gym.country}</option>
              </select>
            </div>
          </div>
        </div>
        <div className="col-sm-3 pre-populated-fields">
          <div>
            <div className="form-group">
              <label className="control-label select">Timezone</label>
              <div className="worx-select-control">
                <select className="form-control" disabled value={profile.tz}>
                  <option>{profile.tz}</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="form-group row">
        <div className="col-sm-3">
          <label className="control-label">Mass</label>
        </div>
        <div className="col-sm-9">
          <RadioButtons
            options={profile.mass_unit_select_options}
            value={profile.mass}
            onChange={(val) => onValueChange(val, 'mass')}
            getValue={(item) => item[1]}
            getLabel={(item) => item[0]}
          />
        </div>
      </div>
      <div className="form-group row">
        <div className="col-sm-3">
          <label className="control-label">Measurement</label>
        </div>
        <div className="col-sm-9">
          <RadioButtons
            options={profile.measurement_unit_select_options}
            value={profile.measurement}
            onChange={(val) => onValueChange(val, 'measurement')}
            getValue={(item) => item[1]}
            getLabel={(item) => item[0]}
          />
        </div>
      </div>
      <div className="form-group row">
        <div className="col-sm-3">
          <label className="control-label">Barcode type</label>
          <div className="helper-text">for Member ID cards</div>
        </div>
        <div className="col-sm-9">
          <RadioButtons
            options={profile.barcode_select_options}
            value={profile.barcode_type}
            onChange={(val) => onValueChange(val, 'barcode_type')}
            getValue={(item) => item[1]}
            getLabel={(item) => item[0]}
          />
        </div>
      </div>
      <h4 className="light">Other</h4>
      <div className="form-group clearfix">
        <div className="col-sm-3">
          <label>Default Reply Address</label>
        </div>
        <div className="col-sm-3">
          <Select
            options={defaultReplyAddresses}
            value={
              profile.gym_contact_detail_attributes &&
              profile.gym_contact_detail_attributes.default_from_address
            }
            onChange={(value) =>
              onValueChange(value, 'gym_contact_detail_attributes.default_from_address')
            }
          />
        </div>
        <div className="col-sm-5 helper-text first-one">
          This setting is applied wherever you send emails to customers.
          <br />
          {profile?.gym_contact_detail_attributes?.default_from_address &&
            defaultReplyAddresses.length && (
              <div>
                {isEmailAuthorized ? (
                  <span className="helper-text">
                    <strong>Verified Sender:</strong> Using this address will improve delivery
                    rates.
                  </span>
                ) : (
                  <span className="alert alert-danger is-sm">
                    <strong>Not verified:</strong> This address has not been verified; delivery
                    rates may be impaired
                    <a
                      style={{ color: 'red' }}
                      href="https://intercom.help/clubworx/en/articles/8940795-email-domain-authentication"
                      target="_blank"
                    >
                      {' '}
                      (more info)
                    </a>
                    .
                  </span>
                )}
              </div>
            )}
        </div>
      </div>
      <Button onClick={save} variant="success">
        Save
      </Button>
    </div>
  );
};

angular.module('gym').value('GymProfileGeneral', GymProfileGeneral);
